import { autoUpdate, computePosition, flip, shift } from '@floating-ui/react-dom';
import type { Editor } from '@tiptap/core';
import { findParentNodeClosestToPos } from '@tiptap/core';
import { posToDOMRect, ReactRenderer } from '@tiptap/react';
// eslint-disable-next-line import/no-named-as-default
import Suggestion from '@tiptap/suggestion';
import { PluginKey } from 'prosemirror-state';
import type React from 'react';

import type { ExtensionProps, RicosExtension, RicosExtensionConfig } from './../../../ricos-types';
import { Node_Type } from './../../../ricos-types';
import { PmNodeType } from './../../../_shared/pm-node-types';
import type { TiptapAdapterServices } from './../../../tiptap-editor/initializeTiptapAdapter';
import { createRicosFunctionalExtension } from './../../../tiptap-extensions';

import { QuickActionsModal } from '../modal/modal';
import type {
  QuickActionsEditorConfig,
  QuickActionsModalProps,
  QuickActionsModalRef,
} from '../types';

export const getTipTapExtensions = (config: QuickActionsEditorConfig) => [
  createRicosFunctionalExtension({
    name: 'quick-actions',
    type: 'extension',
    groups: [],
    placeholder: {
      predicate: ({ doc, selection, node, pos, editor }) => {
        const $pos = doc.resolve(pos);

        const { firstChild } = doc;
        const isEditorEmpty =
          doc.childCount === 1 &&
          firstChild &&
          firstChild.type.name === Node_Type.PARAGRAPH &&
          firstChild.childCount === 0;

        if (isEditorEmpty && !editor.isFocused) {
          return false;
        }

        const isEmptyTopLevelParagraph =
          $pos.parent.type.name === 'doc' &&
          !selection.$head.nodeBefore &&
          !selection.$head.nodeAfter &&
          node.type.name === Node_Type.PARAGRAPH;

        const isEmpty = !node.isLeaf && !node.childCount;

        const isSelected = selection.from - 1 === pos && selection.to + 1 === pos + node.nodeSize;

        return isEmptyTopLevelParagraph && isEmpty && isSelected;
      },
      content: config?.customPlaceholder ?? 'QuickActionsPlugin_EditorPlaceholder',
    },
    reconfigure: (
      config: RicosExtensionConfig<any, any>,
      _extensions: RicosExtension[],
      _ricosProps: ExtensionProps,
      _settings: Record<string, unknown>,
      services: TiptapAdapterServices
    ) => {
      return {
        ...config,
        addOptions: () => {
          return { services };
        },
      };
    },

    createExtensionConfig() {
      return {
        name: 'quick-actions',
        priority: 110,

        addProseMirrorPlugins() {
          return [
            Suggestion({
              editor: this.editor,
              pluginKey: new PluginKey('quickActions'),
              char: '/',
              allowSpaces: true,
              command: ({ editor, props }: { editor: Editor; props: QuickActionsModalProps }) => {
                const { text } = props;
                const query = text ? text : '/';
                const { dispatch, state } = editor.view;
                const { tr } = state;
                const { $from } = tr.selection;
                const range = $from.blockRange();
                if (!range) {
                  return;
                }

                if (
                  !$from.nodeBefore ||
                  $from.nodeBefore?.type.name.toUpperCase() !== 'TEXT' ||
                  !$from.nodeBefore.text?.endsWith(query)
                ) {
                  return;
                }
                if (!dispatch) {
                  return;
                }
                editor.view.dispatch(tr.delete($from.pos - query.length, $from.pos));
              },

              allow: ({ range, state }) => {
                const { doc } = state;
                const { from } = range;
                const $from = doc.resolve(from);
                const pluginsToDisableIn: string[] = [PmNodeType.TABLE_CELL, PmNodeType.CAPTION];
                const disableQuickActions = !!findParentNodeClosestToPos($from, node =>
                  pluginsToDisableIn.includes(node.type.name)
                );

                return !disableQuickActions;
              },

              render: () => {
                let component: ReactRenderer<
                  QuickActionsModalRef,
                  QuickActionsModalProps & React.RefAttributes<QuickActionsModalRef>
                >;

                let cleanup: () => void;

                return {
                  onStart: props => {
                    component = new ReactRenderer(QuickActionsModal, {
                      editor: this.editor,
                      props,
                    });

                    const element = component.element as HTMLElement;
                    element.style.position = 'absolute';

                    this.options.services.context.portal?.appendChild(element);

                    updatePosition(props.editor, element);
                    cleanup = autoUpdate(
                      {
                        getBoundingClientRect: () =>
                          posToDOMRect(
                            props.editor.view,
                            props.editor.state.selection.from,
                            props.editor.state.selection.to
                          ),
                        contextElement: props.editor.view.dom,
                      },
                      component.element as HTMLElement,
                      () => updatePosition(props.editor, element)
                    );
                  },

                  onUpdate(props) {
                    component.updateProps(props);
                    updatePosition(props.editor, component.element as HTMLElement);
                  },

                  onKeyDown(props) {
                    if (props.event.key === 'Escape') {
                      component.destroy();
                      component.element.remove();
                      cleanup?.();
                      props.view.focus();
                      return true;
                    }

                    if (!component) {
                      return false;
                    }

                    return component.ref?.onKeyDown?.(props) ?? false;
                  },

                  onExit(props) {
                    component?.destroy?.();
                    component.element.remove();
                    cleanup?.();
                    props.editor.view.focus();
                  },
                };
              },
            }),
          ];
        },
      };
    },
  }),
];

const updatePosition = (editor: Editor, element: HTMLElement) => {
  const virtualElement = {
    getBoundingClientRect: () =>
      posToDOMRect(editor.view, editor.state.selection.from, editor.state.selection.to),
  };

  computePosition(virtualElement, element, {
    placement: 'bottom-start',
    strategy: 'absolute',
    middleware: [shift(), flip()],
  }).then(({ x, y, strategy }) => {
    element.style.width = 'max-content';
    element.style.position = strategy;
    element.style.left = `${x}px`;
    element.style.top = `${y}px`;
  });
};
